import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';

export type MediaAccessState = 'waiting' | 'error';
export type MediaAccessErrorKind = 'denied' | 'dismissed' | 'notfound' | 'inuse' | 'unknown';

@Component({
  selector: 'app-webcamaccessnotificationdiv',
  templateUrl: './webcamaccessnotificationdiv.component.html',
  styleUrls: ['./webcamaccessnotificationdiv.component.css']
})
export class WebcamaccessnotificationdivComponent implements OnInit {

  @ViewChild('webcamAccessNotificationDiv') webcamAccessNotificationDiv: ElementRef;
  @Output() retry = new EventEmitter<void>();

  visible = true;
  isSeb = false;
  state: MediaAccessState = 'waiting';
  errorKind: MediaAccessErrorKind = 'unknown';
  errorTitle = '';
  errorMessage = '';
  recoverySteps: string[] = [];
  retrying = false;

  constructor() { }

  ngOnInit(): void {
  }

  showWaiting(isSeb: boolean): void {
    this.isSeb = isSeb;
    this.state = 'waiting';
    this.retrying = false;
    this.visible = true;
  }

  async showError(error: any, isSeb: boolean): Promise<void> {
    this.isSeb = isSeb;
    this.state = 'error';
    this.retrying = false;
    this.visible = true;

    const classified = await this.classifyMediaError(error);
    this.errorKind = classified.kind;
    this.errorTitle = classified.title;
    this.errorMessage = classified.message;
    this.recoverySteps = classified.recoverySteps;
  }

  hide(): void {
    this.visible = false;
    this.retrying = false;
  }

  onRetry(): void {
    if (this.retrying) {
      return;
    }
    this.retrying = true;
    this.retry.emit();
  }

  private async classifyMediaError(error: any): Promise<{
    kind: MediaAccessErrorKind;
    title: string;
    message: string;
    recoverySteps: string[];
  }> {
    const name = error?.name || '';
    const rawMessage = (error?.message || '').toString();

    if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
      const permanentlyDenied = await this.isPermissionPermanentlyDenied();

      if (permanentlyDenied) {
        return {
          kind: 'denied',
          title: 'Camera or microphone access is blocked',
          message: 'Access was blocked previously. You need to allow camera and microphone for this site before you can continue.',
          recoverySteps: this.isSeb
            ? [
              'Quit and reopen Safe Exam Browser, or ask an invigilator for help.',
              'Then click Try again.'
            ]
            : [
              'Click the lock or camera icon in your browser address bar.',
              'Set Camera and Microphone to Allow.',
              'Click Try again.'
            ]
        };
      }

      return {
        kind: 'dismissed',
        title: 'Permission was not granted',
        message: this.isSeb
          ? 'Camera or microphone access was not available. Click Try again. If this keeps happening, ask an invigilator for help.'
          : 'Camera or microphone access was not granted. Click Try again and choose Allow when your browser asks.',
        recoverySteps: this.isSeb
          ? []
          : [
            'When the browser prompt appears, choose Allow (not Block).',
            'If no prompt appears, use the lock or camera icon in the address bar to allow access, then click Try again.'
          ]
      };
    }

    if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
      return {
        kind: 'notfound',
        title: 'No camera or microphone found',
        message: 'We could not find a usable camera or microphone on this device.',
        recoverySteps: [
          'Check that a webcam and microphone are connected and enabled.',
          'Close other apps that might be using them, then click Try again.'
        ]
      };
    }

    if (name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError') {
      return {
        kind: 'inuse',
        title: 'Camera or microphone is unavailable',
        message: 'Your camera or microphone could not be started. Another application may be using them.',
        recoverySteps: [
          'Close other apps that might be using the camera or microphone (video calls, other browsers, etc.).',
          'Then click Try again.'
        ]
      };
    }

    return {
      kind: 'unknown',
      title: 'Unable to access camera and microphone',
      message: rawMessage || 'Something went wrong while requesting camera and microphone access.',
      recoverySteps: [
        'Check your camera and microphone, then click Try again.',
        'If the problem continues, restart your browser or ask an invigilator for help.'
      ]
    };
  }

  private async isPermissionPermanentlyDenied(): Promise<boolean> {
    try {
      const permissions = (navigator as any).permissions;
      if (!permissions?.query) {
        return false;
      }

      const [camera, microphone] = await Promise.all([
        permissions.query({ name: 'camera' }).catch(() => null),
        permissions.query({ name: 'microphone' }).catch(() => null)
      ]);

      return camera?.state === 'denied' || microphone?.state === 'denied';
    }
    catch {
      return false;
    }
  }

}
